import React, { useEffect, useState } from 'react'
import { Stack, Typography } from '@mui/material'
import {
  type IChartApi, type ISeriesApi, type MouseEventHandler,
  type MouseEventParams, type SingleValueData, type Time, type UTCTimestamp
} from 'lightweight-charts'
import { formatToLocalMilliTimestampFromUTCNumber } from '../../utils/time-utils'

interface LiveCrosshairSummaryProps {
  chartObj: IChartApi | null | undefined
  symbols: string[]
  lineSeriesMap: Map<string, ISeriesApi<'Line'>>
}

const LiveCrosshairSummary: React.FC<LiveCrosshairSummaryProps> = ({ chartObj, symbols, lineSeriesMap }) => {
  const [mouseEventParameter, setMouseEventParameters] = useState<MouseEventParams>()
  const [summaryData, setSummaryData] = useState<Record<string, SingleValueData>>({})
  const [summaryTime, setSummaryTime] = useState<Time | undefined>()

  const crosshairMoveHandler: MouseEventHandler<any> = param => {
    setMouseEventParameters(param)
  }

  useEffect(() => {
    chartObj?.unsubscribeCrosshairMove(crosshairMoveHandler)
    chartObj?.subscribeCrosshairMove(crosshairMoveHandler)
  }, [chartObj])

  useEffect(() => {
    if (mouseEventParameter == null || mouseEventParameter.time == null) {
      return
    }

    let summaryTime: Time | undefined
    const summaryData = {}

    symbols
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .forEach(symbol => {
        const lineSeries = lineSeriesMap.get(symbol)
        if (lineSeries != null) {
          // @ts-expect-error missing typing
          summaryData[symbol] = mouseEventParameter.seriesData.get(lineSeries)

          if (summaryTime == null) {
            summaryTime = mouseEventParameter.seriesData.get(lineSeries)?.time
          }
        }
      })

    setSummaryTime(summaryTime)
    setSummaryData(summaryData)
  }, [symbols, mouseEventParameter])

  return (
    <Stack direction="column" sx={{ height: 1, minWidth: '400px', overflowY: 'scroll', marginRight: 1 }}>
      <Typography>
        {formatToLocalMilliTimestampFromUTCNumber(summaryTime as UTCTimestamp * 1000)}
      </Typography>
      {
        Object.entries(summaryData)
          .sort(([key1, value1], [key2, value2]) => {
            if (value1 != null && value2 != null) {
              return (value1.value === value2.value) ? 0 : (value1.value > value2.value ? -1 : 1)
            } else if (value1 != null) {
              return -1
            } else {
              return 1
            }
          })
          .map(([symbol, data]) => {
            if (lineSeriesMap.get(symbol) == null) {
              return null
            }
            return (
              <Stack direction="row" key={symbol} alignItems="center" gap={1}>
                <span style={{
                  height: '10px',
                  width: '10px',
                  backgroundColor: lineSeriesMap.get(symbol)?.options().color,
                  borderRadius: '50%',
                  display: 'inline-block'
                }}>
                </span>
                <Typography display="inline-block" sx={{ minWidth: '300px' }}>
                  {symbol}
                </Typography>
                <Typography display="inline-block" sx={{ minWidth: '50px' }}>
                  {data?.value}
                </Typography>
              </Stack>
            )
          })
      }
    </Stack>
  )
}

export default LiveCrosshairSummary
